import React, {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Alert, AlertColor, Snackbar} from '@mui/material';
import {setConnectionErrorHandler} from "../core/connectionErrorBridge";
import {useTranslation} from "react-i18next";

interface SnackbarContextType {
    showSnackbar: (message: string, severity?: AlertColor) => void;
    showConnectionError: () => void;
    hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
    children: ReactNode;
}

export const SnackbarProvider = ({children}: SnackbarProviderProps) => {
    const {t} = useTranslation();

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<AlertColor>('info');
    const isConnectionErrorRef = useRef(false);

    const showSnackbar = useCallback((message: string, severity: AlertColor = 'info') => {
        if (!message) return;
        if (isConnectionErrorRef.current) return;

        setMessage(message);
        setSeverity(severity);
        setOpen(true);
    }, []);

    const showConnectionError = useCallback(() => {
        isConnectionErrorRef.current = true;
        setMessage(t('errors.connection.refused') as string);
        setSeverity('error');
        setOpen(true);
    }, [t]);

    useEffect(() => {
        setConnectionErrorHandler(showConnectionError);
        return () => setConnectionErrorHandler(null);
    }, [showConnectionError]);

    const hideSnackbar = useCallback(() => {
        isConnectionErrorRef.current = false;
        setOpen(false);
    }, []);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        hideSnackbar();
    };

    return (
        <SnackbarContext.Provider value={{showSnackbar, showConnectionError, hideSnackbar}}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={10000}
                onClose={handleClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                sx={{mb: 2, mr: 2}}
            >
                <Alert onClose={handleClose} severity={severity}>
                    {message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};
